/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect, useState } from "react";
import { useStoreDispatch } from "../../../../store/hooks";
import { setBasicDialogOpenFor, setErrorMsg } from "../../../../store/helpers/helperReducers";
import { IThresholdCrossingSettingRule, IKpi } from "../../../../store/api/admin/types";
import { useThresholdCrossingSettingRuleData } from "./hooks";
import { isError } from "../../../../utils/validations";
import { Sheet } from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { RadixSelectControl } from "../../../ui/radix-select";

interface IAddEditThresholdCrossingSettingRuleProps {
  selectedRow: IThresholdCrossingSettingRule;
  modelOpenMode?: string;
}
interface ISelectOptions {
  text: string;
  value: string;
}

const AddEditProfileRule: React.FC<IAddEditThresholdCrossingSettingRuleProps> = ({ modelOpenMode = "Add", selectedRow }) => {
  const dispatch = useStoreDispatch();
  const [kpiLists, setKpiLists] = useState<ISelectOptions[]>([]);
  const [kpiThresholdAbove, setKpiThresholdAbove] = useState("");
  const [kpiMinValue, setKpiMinValue] = useState(0);
  const [kpiMaxValue, setKpiMaxValue] = useState(0);

  const {
    thresholdCrossingSettingRuleData,
    setThresholdCrossingSettingRuleData,
    submitHandler,
    onBlurHandler,
    errMsg,
    setErrMsg,
    errorsPresent,
    kpi,
    kpiType,
    operator,
    thresholdAbove,
    thresholdBelow,
    consecutiveSamples,
    LteKpiList,
    NrKpiList,
  } = useThresholdCrossingSettingRuleData(selectedRow);

  const handleRuleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setThresholdCrossingSettingRuleData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrMsg((prev) => ({ ...prev, [name]: "" }));
  };

  const handleInputFieldRuleChange = (name: string, value: string) => {
    setThresholdCrossingSettingRuleData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrMsg((prev) => ({ ...prev, [name]: "" }));
  };

  useEffect(() => {
    if (thresholdCrossingSettingRuleData.kpiType) {
      if (thresholdCrossingSettingRuleData.kpiType === "LTE") {
        setKpiLists(
          LteKpiList.map((kpi) => ({
            text: kpi.kpi,
            value: kpi.kpi,
          }))
        );

        const kpiItem = LteKpiList.filter((k) => k.kpi == thresholdCrossingSettingRuleData.kpi);
        if (kpiItem.length) {
          setKpiThresholdAbove(kpiItem[0].thresholdAbove ? "YES" : "NO");
          setKpiMinValue(kpiItem[0].minValue);
          setKpiMaxValue(kpiItem[0].maxValue);
        }
      } else if (thresholdCrossingSettingRuleData.kpiType === "NR") {
        setKpiLists(
          NrKpiList.map((kpi) => ({
            text: kpi.kpi,
            value: kpi.kpi,
          }))
        );
        const kpiItem = NrKpiList.filter((k) => k.kpi == thresholdCrossingSettingRuleData.kpi);
        if (kpiItem.length) {
          setKpiThresholdAbove(kpiItem[0].thresholdAbove ? "YES" : "NO");
          setKpiMinValue(kpiItem[0].minValue);
          setKpiMaxValue(kpiItem[0].maxValue);
        }
      }
    }
  }, [thresholdCrossingSettingRuleData.kpiType]);

  const handleChangeRuleKpi = (name: string, value: string) => {
    if (name === "kpiType") {
      if (value === "LTE") {
        setKpiLists(
          LteKpiList.map((kpi) => ({
            text: kpi.kpi,
            value: kpi.kpi,
          }))
        );
      } else if (value === "NR") {
        setKpiLists(
          NrKpiList.map((kpi) => ({
            text: kpi.kpi,
            value: kpi.kpi,
          }))
        );
      }
    }

    if (name === "kpi") {
      const sourceList =
        thresholdCrossingSettingRuleData.kpiType === "LTE"
          ? LteKpiList
          : thresholdCrossingSettingRuleData.kpiType === "NR"
          ? NrKpiList
          : [];

      const kpiItem = sourceList.find((k) => k.kpi === value);
      if (kpiItem) {
        setKpiThresholdAbove(kpiItem.thresholdAbove ? "YES" : "NO");
        setKpiMinValue(kpiItem.minValue);
        setKpiMaxValue(kpiItem.maxValue);
      }
    }

    setThresholdCrossingSettingRuleData((prev) => ({
      ...prev,
      thresholdAbove: null,
      thresholdBelow: null,
      [name]: value,
    }));

    setErrMsg((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const isDirty = (
    thresholdCrossingSettingRuleData.kpiType !== selectedRow.kpiType ||
    thresholdCrossingSettingRuleData.kpi !== selectedRow.kpi ||
    thresholdCrossingSettingRuleData.thresholdAbove !== selectedRow.thresholdAbove ||
    thresholdCrossingSettingRuleData.thresholdBelow !== selectedRow.thresholdBelow ||
    thresholdCrossingSettingRuleData.consecutiveSamples !== selectedRow.consecutiveSamples
  );

  const hasThresholdValue =
    (thresholdCrossingSettingRuleData.thresholdAbove !== null && thresholdCrossingSettingRuleData.thresholdAbove !== undefined && String(thresholdCrossingSettingRuleData.thresholdAbove).trim() !== "") ||
    (thresholdCrossingSettingRuleData.thresholdBelow !== null && thresholdCrossingSettingRuleData.thresholdBelow !== undefined && String(thresholdCrossingSettingRuleData.thresholdBelow).trim() !== "");

  const validationPassed = 
    !!thresholdCrossingSettingRuleData.kpiType &&
    !!thresholdCrossingSettingRuleData.kpi &&
    hasThresholdValue &&
    !!thresholdCrossingSettingRuleData.consecutiveSamples;

  const isDiabledSave =
    isError(errMsg) ||
    !validationPassed ||
    (modelOpenMode === "Edit" && !isDirty);

  const kpiTypeList = [
    { text: "LTE", value: "LTE" },
    { text: "NR", value: "NR" },
  ];

  const handleClose = () => {
    dispatch(setErrorMsg({}));
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose}>
        Cancel
      </Button>
      <Button onClick={submitHandler} disabled={isDiabledSave || errorsPresent}>
        Save
      </Button>
    </>
  );

  return (
    <Sheet
      open={true}
      onClose={handleClose}
      title={`${modelOpenMode} ${modelOpenMode?.toLowerCase() !== "add" ? "rule" : "rule"}`}
      footer={footer}
      width="520px"
    >
      <form autoComplete="off">
        <div className="space-y-5 pt-2">
          {/* KPI Type */}
          <div className="space-y-2">
            <Label htmlFor="rule-kpiType">
              KPI Type <span style={{ color: "var(--destructive)" }}>*</span>
            </Label>
            <RadixSelectControl
              options={[{ label: "Select KPI type", value: "placeholder" }, ...kpiTypeList.map(item => ({ label: item.text, value: item.value }))]}
              value={thresholdCrossingSettingRuleData?.kpiType || "placeholder"}
              onValueChange={(val) => handleChangeRuleKpi(kpiType, val === "placeholder" ? "" : val)}
              autoFocus={true}
            />
            {errMsg?.kpiType && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.kpiType}</p>}
          </div>

          {/* KPI */}
          <div className="space-y-2">
            <Label htmlFor="rule-kpi">
              KPI <span style={{ color: "var(--destructive)" }}>*</span>
            </Label>
            <RadixSelectControl
              options={[{ label: "Select KPI", value: "placeholder" }, ...kpiLists.map(item => ({ label: item.text, value: item.value }))]}
              value={thresholdCrossingSettingRuleData?.kpi || "placeholder"}
              onValueChange={(val) => handleChangeRuleKpi(kpi, val === "placeholder" ? "" : val)}
            />
            {errMsg?.kpi && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.kpi}</p>}
          </div>

          {/* Alert When Above */}
          {kpiThresholdAbove == "YES" && (
            <div className="space-y-2">
              <Label htmlFor="rule-thresholdAbove">Alert When Above</Label>
              <Input
                id="rule-thresholdAbove"
                name={thresholdAbove}
                type="text"
                value={thresholdCrossingSettingRuleData?.thresholdAbove ?? ""}
                onChange={handleRuleChange}
                onBlur={onBlurHandler}
                min={kpiMinValue}
                max={kpiMaxValue}
              />
              {errMsg?.thresholdAbove && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.thresholdAbove}</p>}
            </div>
          )}

          {/* Alert When Below */}
          {kpiThresholdAbove == "NO" && (
            <div className="space-y-2">
              <Label htmlFor="rule-thresholdBelow">Alert When Below</Label>
              <Input
                id="rule-thresholdBelow"
                name={thresholdBelow}
                type="text"
                value={thresholdCrossingSettingRuleData?.thresholdBelow ?? ""}
                onChange={handleRuleChange}
                onBlur={onBlurHandler}
                min={kpiMinValue}
                max={kpiMaxValue}
              />
              {errMsg?.thresholdBelow && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.thresholdBelow}</p>}
            </div>
          )}

          {/* Consecutive Samples */}
          <InputField
            label="Consecutive Samples"
            name={consecutiveSamples}
            required={true}
            onChange={handleInputFieldRuleChange}
            onBlur={onBlurHandler}
            type="number"
            value={thresholdCrossingSettingRuleData?.consecutiveSamples}
            min={1}
            max={10}
          />
        </div>
      </form>
    </Sheet>
  );
};

export default AddEditProfileRule;
